import { search } from '../search-form';
import { removeElements } from '../../dom/remove';
import { clearInputState } from '../../forms/input';
import { navigateTo } from '../../http/redirect';
import SearchThisArea from '../components/search-this-area';
import MapSearch from '../search-map';
import PropertySearchFilter, { locationFilters } from '../../property/filters';

const onHistoryPopstate = async ( ctx: MapSearch, event: CustomEvent ) => {
    const previousState = event.detail.previousState;

    if ( previousState && previousState.isRoutedPopup ) {
        // no reason to re-search as the state has not changed
        // the user has been on a property / contact popup
        return;
    }

    const isMapMode = ( 'firstMapView' in event.detail.state.meta );

    if ( !isMapMode ) {
        const url = new URL( event.detail.state.url );
        url.hash = '';

        navigateTo( url.toString() );
        return;
    }

    let {
        currentLocationID,
        currentLocationType,
    } = event.detail.state.meta.propertySearchParams;

    currentLocationID = parseInt( currentLocationID || '0' ) || 0;
    currentLocationType = currentLocationType || '';

    const { viewMode } = event.detail.state.meta;

    const propertySearchParams = event.detail.state.meta.propertySearchParams;

    const urlSearchParams = new URLSearchParams( propertySearchParams );

    const filter = PropertySearchFilter.fromURLSearchParams( urlSearchParams );

    const paramsToIgnore = [ 'mode' ];

    const params = {};

    for ( const [ key, value ] of urlSearchParams ) {
        if ( paramsToIgnore.includes( key ) ) {
            continue;
        }
        params[ key ] = value;
    }

    const hasCompleteLocationInfo = currentLocationID > 0;

    const locationIDElements = document.querySelectorAll<HTMLInputElement>( '.property-search-form [name=locationId]' );

    if ( hasCompleteLocationInfo ) {
        let latitude: number | undefined;
        let longitude: number | undefined;

        const hasLatLon = (
            propertySearchParams &&
            propertySearchParams.latitude &&
            propertySearchParams.longitude
        );

        if ( hasLatLon ) {
            latitude = parseFloat( propertySearchParams.latitude );
            longitude = parseFloat( propertySearchParams.longitude );
        }

        for ( let i = 0; i < locationIDElements.length; i++ ) {
            if ( filter.anyLocationFilters() ) {
                locationIDElements[ i ].value = `location-filters+${currentLocationID}`;
            } else {
                locationIDElements[ i ].value = currentLocationID;
            }

            locationIDElements[ i ].dataset.id = currentLocationID;
            locationIDElements[ i ].dataset.type = currentLocationType;

            if ( latitude && longitude ) {
                locationIDElements[ i ].dataset.latitude = latitude.toString();
                locationIDElements[ i ].dataset.longitude = longitude.toString();
            }

            if ( event.detail.state.meta.location ) {
                locationIDElements[ i ].dataset.location = JSON.stringify( event.detail.state.meta.location );
            }
        }
    } else if ( filter.anyLocationFilters() ) {
        for ( let i = 0; i < locationIDElements.length; i++ ) {
            locationIDElements[ i ].value = 'location-filters';
        }
    }

    const locationTexts = document.getElementsByName( 'locationFreeText' ) as NodeListOf<HTMLInputElement>;

    if ( !hasCompleteLocationInfo && locationTexts.length > 0 && viewMode !== SearchThisArea.id ) {
        for ( let t = 0; t < locationTexts.length; t++ ) {
            locationTexts[ t ].value = '';
            locationTexts[ t ].dataset.tsGeo = '';

            if ( locationTexts[ t ].parentElement && ( locationTexts[ t ].parentElement as HTMLElement ).parentElement ) {
                const resetBtn = ( ( locationTexts[ t ].parentElement as HTMLElement ).parentElement as HTMLElement ).querySelector( '.reset-field' ) as HTMLElement;

                if ( resetBtn ) {
                    resetBtn.style.visibility = 'hidden';
                }
            }
        }
    }

    if ( !hasCompleteLocationInfo && locationIDElements.length > 0 && viewMode !== SearchThisArea.id ) {
        for ( let k = 0; k < locationIDElements.length; k++ ) {
            clearInputState( locationIDElements[ k ] );
        }
    }

    const availableQueryParams = [
        'minprice',
        'maxprice',
        'beds',
        'baths',
        'build',
        'plot',
        'radius',
        'order',
        ...locationFilters.all(),
    ];

    for ( let p = 0; p < availableQueryParams.length; p++ ) {
        const param = availableQueryParams[ p ];

        if ( params[ param ] ) {
            continue;
        }

        const paramInputs = document.querySelectorAll<HTMLInputElement>( `[name="${param}"]` );

        if ( paramInputs.length === 0 ) {
            continue;
        }

        for ( let i = 0; i < paramInputs.length; i++ ) {
            const input = paramInputs[ i ];
            input.value = '';

            if ( input.nodeName === 'SELECT' ) {
                input.classList.remove( 'has-value' );
            }
        }
    }

    const tagLikeInputs = [
        'tags',
    ];

    tagLikeInputs.forEach( ( name ) => {
        if ( params[ name ] ) {
            document.querySelectorAll<HTMLInputElement>( `[name="${name}"][type="hidden"]` ).forEach( ( input ) => input.remove() );

            const values = params[ name ].split( ',' );

            values.forEach( ( value: string ) => {
                document.querySelectorAll<HTMLInputElement>( `[name="${name}"][value="${value}"][type="checkbox"]` ).forEach( ( input: HTMLInputElement ) => input.checked = true );

                const inputToCreate = document.createElement( 'input' );
                inputToCreate.type = 'hidden';
                inputToCreate.name = name;
                inputToCreate.value = value;
                ctx.baseForm.appendChild( inputToCreate );
            } );
        } else {
            document.querySelectorAll<HTMLInputElement>( `[name="${name}"][type="hidden"]` ).forEach( ( input: HTMLInputElement ) => input.remove() );
            document.querySelectorAll<HTMLInputElement>( `[name="${name}"][type="checkbox"]` ).forEach( ( input: HTMLInputElement ) => input.checked = false );
        }
    } );

    if ( propertySearchParams ) {
        const propertyTypesCSV = propertySearchParams[ 'types' ] || '';

        const propertyTypes = propertyTypesCSV.split( ',' ) || [];

        const propertyTypeInputs = document.querySelectorAll<HTMLInputElement>( 'input[name="types"]' );

        for ( let i = 0; i < propertyTypeInputs.length; i++ ) {
            const input = propertyTypeInputs[ i ];

            const hiddenInput = document.querySelector<HTMLInputElement>( `input[type="hidden"][name="types"][value="${input.value}"]` );

            if ( propertyTypes.includes( input.value ) ) {
                input.checked = true;

                if ( !hiddenInput ) {
                    const inputToCreate = document.createElement( 'input' );
                    inputToCreate.type = 'hidden';
                    inputToCreate.name = 'types';
                    inputToCreate.value = input.value;
                    ctx.baseForm.appendChild( inputToCreate );
                }
                continue;
            }

            input.checked = false;

            if ( hiddenInput ) {
                hiddenInput.remove();
            }
        }
    }

    if ( propertySearchParams && propertySearchParams[ 'offer' ] ) {
        const offerType = propertySearchParams[ 'offer' ];
        const offerTypeInputs = document.querySelectorAll<HTMLInputElement | HTMLSelectElement>( '[name="offer"]' );

        for ( let i = 0; i < offerTypeInputs.length; i++ ) {
            offerTypeInputs[ i ].value = offerType;

            if ( offerTypeInputs[ i ].nodeName === 'SELECT' ) {
                offerTypeInputs[ i ].classList.add( 'has-value' );
            }
        }
    }

    const orderByDropdownOptions = document.querySelectorAll<HTMLLIElement>( '.search__orderby > li' );

    if ( !params[ 'order' ] && orderByDropdownOptions.length > 0 ) {
        for ( let i = 0; i < orderByDropdownOptions.length; i++ ) {
            if ( 'default' in orderByDropdownOptions[ i ].dataset ) {
                orderByDropdownOptions[ i ].classList.add( 'active' );
            } else {
                orderByDropdownOptions[ i ].classList.remove( 'active' );
            }
        }
    }

    if ( params[ 'order' ] && orderByDropdownOptions.length > 0 ) {
        for ( let i = 0; i < orderByDropdownOptions.length; i++ ) {
            if ( orderByDropdownOptions[ i ].dataset.value === params[ 'order' ] ) {
                orderByDropdownOptions[ i ].classList.add( 'active' );
            } else {
                orderByDropdownOptions[ i ].classList.remove( 'active' );
            }
        }
    }

    const popupRadiusInput = ctx.popupForm.querySelector<HTMLSelectElement>( '[name=radius]' );
    const baseRadiusInput = ctx.baseForm.querySelector<HTMLInputElement>( '[name=radius]' );

    if ( params[ 'radius' ] && baseRadiusInput ) {
        baseRadiusInput.value = params[ 'radius' ];
    }

    if ( params[ 'radius' ] && !baseRadiusInput ) {
        const input = document.createElement( 'input' );
        input.type = 'hidden';
        input.name = 'radius';
        input.value = params[ 'radius' ];
        ctx.baseForm.appendChild( input );
    }

    if ( params[ 'radius' ] && popupRadiusInput ) {
        popupRadiusInput.value = params[ 'radius' ];

        const parent = popupRadiusInput.closest( '.searchpopup__location-radius' );

        if ( parent ) {
            parent.classList.remove( 'd-none' );
        }
    }

    if ( !params[ 'radius' ] && baseRadiusInput ) {
        baseRadiusInput.remove();
    }

    if ( !params[ 'radius' ] && popupRadiusInput ) {
        popupRadiusInput.classList.remove( 'has-value' );

        const parent = popupRadiusInput.closest( '.searchpopup__location-radius' );

        if ( parent ) {
            parent.classList.add( 'd-none' );
        }
    }

    if ( params[ 'area' ] ) {
        removeElements( 'input[type=hidden][name=area]' );

        const areaInput = document.createElement( 'input' );
        areaInput.type = 'hidden';
        areaInput.name = 'area';
        areaInput.value = params[ 'area' ];

        ctx.baseForm.appendChild( areaInput );

        if ( !viewMode ) {
            const saimContainer = document.querySelector( '.searchthisarea__container' ) as HTMLElement;

            if ( saimContainer ) {
                saimContainer.style.display = 'none';
            }
        }
    }

    if ( !params[ 'area' ] ) {
        removeElements( 'input[type=hidden][name=area]' );
    }

    locationFilters.all().forEach( ( name: string ) => {
        ctx.baseForm.querySelectorAll( `[name=${name}]` ).forEach( ( input: HTMLInputElement ) => input.remove() );

        if ( params[ name ] ) {
            const values: Array<string> = params[ name ].split( ',' );

            values.forEach( ( value: string ) => {
                const newInput = document.createElement( 'input' );
                newInput.type = 'hidden';
                newInput.name = name;
                newInput.value = value;
                ctx.baseForm.appendChild( newInput );
            } );
        }
    } );

    const paramsToFilterOut = [
        ...tagLikeInputs,
        ...locationFilters.all(),
        'types',
        'order',
        'radius',
    ];

    const paramsFiltered = {};

    Object.keys( params )
        .filter( ( key ) => !paramsToFilterOut.includes( key ) )
        .forEach( ( key ) => {
            paramsFiltered[ key ] = params[ key ];
        } );

    for ( const [ key, value ] of ( Object.entries( paramsFiltered ) as [string, string][] ) ) {
        const name = key;

        const inputs = ctx.baseForm.querySelectorAll<HTMLInputElement | HTMLSelectElement>( `[name=${name}]` );

        if ( inputs.length === 0 ) {
            continue;
        }

        for ( let i = 0; i < inputs.length; i++ ) {
            inputs[ i ].value = value;

            if ( inputs[ i ].nodeName === 'SELECT' ) {
                inputs[ i ].classList.add( 'has-value' );
            }
        }
    }

    const saveSearchButtons = document.querySelectorAll<HTMLButtonElement>( '[data-save-property=property-search]' );

    if ( event.detail.state.meta.saveState &&
        Object.keys( event.detail.state.meta.saveState ).length > 0 &&
        saveSearchButtons.length > 0
    ) {
        for ( let i = 0; i < saveSearchButtons.length; i++ ) {
            Object.keys( event.detail.state.meta.saveState ).forEach( ( dataAttr ) => {
                saveSearchButtons[ i ].dataset[ dataAttr ] = JSON.stringify( event.detail.state.meta.saveState[ dataAttr ] );
            } );
        }
    }

    const isSpainSearch = (
        parseInt( currentLocationID ) === 0 &&
        !params[ 'area' ] &&
        !filter.anyLocationFilters()
    );

    let searchOptions: object = {
        from: ctx.baseForm,
        to: ctx.popupForm,
        triggerStateChange: false,
        forceReset: isSpainSearch,
    };

    const {
        locationBoundNE,
        locationBoundSW,
        parentLocations,
    } = propertySearchParams;

    if ( locationBoundNE && locationBoundSW && parentLocations ) {
        searchOptions = {
            ...searchOptions,
            locationBoundNE,
            locationBoundSW,
            parentLocations,
        };
    }

    if ( hasCompleteLocationInfo ) {
        searchOptions = {
            ...searchOptions,
            currentLocationID,
        };
    }

    if ( filter.anyLocationFilters() ) {
        await ctx.findYourPlace.search( {
            triggerStateChange: false,
        } );
        return;
    }

    if ( viewMode === SearchThisArea.id ) {
        const detail = {
            originalEvent: event.detail.originalEvent,
            stateBeforeActivation: {},
            setPreviousSearch: true,
        };

        ctx.root.dispatchEvent( new CustomEvent( `twc:mapsearch:${SearchThisArea.id}:activate`, {
            bubbles: true,
            detail: detail,
        } ) );
        return;
    }

    for ( const name of ctx.activeComponents ) {
        ctx.root.dispatchEvent( new CustomEvent( `twc:mapsearch:${name}:deactivate`, {
            bubbles: true,
        } ) );
    }

    return search( ctx, null, searchOptions );
};

export default onHistoryPopstate;
